import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { mapPostRow } from '@/lib/mapPost'
import PostCard from '@/components/features/feed/PostCard'
import SearchBar from '@/components/ui/SearchBar'
import { CATS, type PostData } from '@/data/mockData'
import { useHorizontalScroll } from '@/hooks/useHorizontalScroll'

const C = { teal: '#0F5C5C', live: '#D85F5F', ink: '#202523', ink2: '#6D7471', border: '#E1E5E3' }
const FH = "'Plus Jakarta Sans', sans-serif"
const FB = "'Inter', sans-serif"

export default function NowPage() {
  const [catFilter, setCatFilter] = useState<string | null>(null)
  const catsScrollRef = useHorizontalScroll<HTMLDivElement>()

  // Datos reales de Supabase (antes usaba datos de ejemplo)
  const { data, isLoading } = useQuery({
    queryKey: ['now-feed'],
    queryFn: async () => {
      if (!supabase) return { posts: [], businesses: [] }
      const { data: businesses } = await supabase
        .from('businesses')
        .select('id, name, zone, verified, cover_colors, emoji')
      const { data: posts } = await supabase
        .from('posts')
        .select('*')
        .eq('is_active', true)
        .eq('is_permanent', false)
        .or(`ends_at.is.null,ends_at.gt.${new Date().toISOString()}`)
        .order('created_at', { ascending: false })
      return { posts: posts ?? [], businesses: businesses ?? [] }
    },
    staleTime: 1000 * 30,
    refetchInterval: 1000 * 60,
  })

  const feed: PostData[] = data
    ? data.posts.map((p) =>
        mapPostRow(
          p,
          data.businesses.find((b) => b.id === p.business_id)
        )
      )
    : []

  // Solo las categorías que de verdad tienen actividades ahora mismo
  const availableCats = CATS.filter((c) => feed.some((p) => p.cat === c.k))

  const matchesCat = (p: PostData) => !catFilter || p.cat === catFilter
  const live = feed.filter((p) => p.type === 'live' && matchesCat(p)).slice(0, 4)
  const active = feed.filter((p) => p.type === 'promo' && matchesCat(p)).slice(0, 4)
  const soon = feed.filter((p) => p.type === 'event' && matchesCat(p)).slice(0, 4)
  const time = new Date().toLocaleTimeString('es-PA', { hour: '2-digit', minute: '2-digit' })

  return (
    <div style={{ maxWidth: 1600, margin: '0 auto', padding: '28px 28px 60px' }}>
      <SearchBar style={{ marginBottom: 20 }} />
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
        <span
          style={{
            width: 12,
            height: 12,
            borderRadius: 99,
            background: C.live,
            animation: 'pxPulse 1.4s infinite',
            display: 'inline-block',
          }}
        />
        <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 28, letterSpacing: '-0.02em' }}>
          Ahora
        </div>
        <div style={{ fontSize: 14, color: C.ink2, alignSelf: 'flex-end', paddingBottom: 4 }}>
          {time} · Pereira, Risaralda
        </div>
      </div>

      {/* Filtro por tipo de actividad */}
      <div
        ref={catsScrollRef}
        style={{
          display: 'flex',
          gap: 8,
          overflowX: 'auto',
          scrollbarWidth: 'none',
          marginBottom: 24,
        }}
      >
        <button
          onClick={() => setCatFilter(null)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            whiteSpace: 'nowrap',
            padding: '8px 14px',
            borderRadius: 99,
            flexShrink: 0,
            border: `1px solid ${!catFilter ? C.teal : C.border}`,
            background: !catFilter ? C.teal : '#fff',
            color: !catFilter ? '#fff' : C.ink,
            fontFamily: FB,
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          Todas
        </button>
        {availableCats.map((c) => (
          <button
            key={c.k}
            onClick={() => setCatFilter(catFilter === c.k ? null : c.k)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              whiteSpace: 'nowrap',
              padding: '8px 14px',
              borderRadius: 99,
              flexShrink: 0,
              border: `1px solid ${catFilter === c.k ? C.teal : C.border}`,
              background: catFilter === c.k ? C.teal : '#fff',
              color: catFilter === c.k ? '#fff' : C.ink,
              fontFamily: FB,
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            <span>{c.emoji}</span>
            {c.label}
          </button>
        ))}
      </div>

      {/* En vivo */}
      <div
        style={{
          fontFamily: FH,
          fontWeight: 700,
          fontSize: 17,
          marginBottom: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        📡 Sucediendo ahora
        <span
          style={{ fontSize: 12, color: C.live, fontWeight: 700, animation: 'pxPulse 2s infinite' }}
        >
          ● transmitiendo
        </span>
      </div>
      {isLoading ? (
        <EmptyNote text="Cargando actividades…" />
      ) : live.length === 0 ? (
        <EmptyNote text="Nada en vivo con este filtro por ahora." />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))',
            gap: 16,
            marginBottom: 28,
          }}
        >
          {live.map((p) => (
            <PostCard key={p.id} post={p} />
          ))}
        </div>
      )}

      {/* Promos activas */}
      <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 17, marginBottom: 14 }}>
        🏷️ Promociones activas
      </div>
      {isLoading ? (
        <EmptyNote text="Cargando actividades…" />
      ) : active.length === 0 ? (
        <EmptyNote text="No hay promociones activas con este filtro." />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))',
            gap: 16,
            marginBottom: 28,
          }}
        >
          {active.map((p) => (
            <PostCard key={p.id} post={p} />
          ))}
        </div>
      )}

      {/* A punto de comenzar */}
      <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 17, marginBottom: 14 }}>
        ⏰ A punto de comenzar
      </div>
      {isLoading ? (
        <EmptyNote text="Cargando actividades…" />
      ) : soon.length === 0 ? (
        <EmptyNote text="Nada por comenzar con este filtro todavía." />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))',
            gap: 16,
          }}
        >
          {soon.map((p) => (
            <PostCard key={p.id} post={p} />
          ))}
        </div>
      )}
    </div>
  )
}

function EmptyNote({ text }: { text: string }) {
  return (
    <div
      style={{
        padding: '18px 20px',
        marginBottom: 28,
        borderRadius: 12,
        background: '#fff',
        border: `1px solid ${C.border}`,
        color: C.ink2,
        fontSize: 13.5,
      }}
    >
      {text}
    </div>
  )
}
